import React, { useEffect, useState, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog, DialogContent, DialogTitle, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Paper, FormControl, InputLabel, Select, MenuItem,
  InputAdornment, IconButton, Typography, Box, Chip, Tooltip, Button,
  Switch, FormControlLabel, useMediaQuery, RadioGroup, Radio,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import EditIcon from '@mui/icons-material/Edit';
import LockResetIcon from '@mui/icons-material/LockReset';
import Layout from '../../components/layout/Layout';
import DeleteModel from '../../components/deletemodel/DeleteModel';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  deleteUser, getUsers, userFlagOff, userRegister,
  approveUser, rejectUser, changeUserRole, updateUser, adminSendResetEmail,
} from '../../Redux/Actions/userAction';
import { createStoreFormLink } from '../../Redux/Actions/storeAction';
import { createMenuFormLink } from '../../Redux/Actions/menuMasterAction';
import './user.css';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../../api/api';

const STATUS_MAP = {
  pending:  { label: '승인 대기', bg: '#fff8e1', color: '#f57f17' },
  approved: { label: '승인됨',   bg: '#e8f5e9', color: '#2e7d32' },
  rejected: { label: '거절됨',   bg: '#fce4ec', color: '#c62828' },
};

const ROLE_MAP = {
  admin:         'Admin',
  user:          '일반',
  designer:      'Designer',
  store_owner:   'Store owner',
  store_manager: 'Store manager',
  tlj_admin:     'TLJ admin',
  tlj_manager:   'TLJ Manager',
};

/** 그룹 순서: admin, designer, Store owner, store manager, TLJ admin, TLJ Manager, 일반(user) */
const GROUP_ORDER = ['admin', 'designer', 'store_owner', 'store_manager', 'tlj_admin', 'tlj_manager', 'user'];
const ROLES = ['admin', 'designer', 'store_owner', 'store_manager', 'tlj_admin', 'tlj_manager'];

function StatusChip({ status }) {
  const cfg = STATUS_MAP[status] || { label: status, bg: '#eee', color: '#555' };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{ background: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: { xs: 10, sm: 11 }, maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', lineHeight: 1.2, px: 0.75 } }}
    />
  );
}

function User({ user }) {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width:720px)');
  const { users, isUserAdded, isAlreadyAvailable, isUserDeleted } =
    useSelector(s => s.User);

  const [openForm, setOpenForm] = useState(false);
  const [openDeleteModel, setOpenDeleteModel] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const [roleDialogUser, setRoleDialogUser] = useState(null);
  const [newRole, setNewRole] = useState('user');
  const [approveDialogUser, setApproveDialogUser] = useState(null);
  const [approveMode, setApproveMode] = useState('onboard');
  const [approveStoreId, setApproveStoreId] = useState('');
  const [approveStores, setApproveStores] = useState([]);
  const navigate = useNavigate();
  const [approveRole, setApproveRole] = useState('store_owner');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showPassword, setShowPassword] = useState(false);
  const [role, setRole] = useState('user');
  /** true: 비밀번호 직접 입력, false: 고객에게 초기 비밀번호 설정 이메일 발송 */
  const [usePasswordMode, setUsePasswordMode] = useState(true);
  const [formLinkDialog, setFormLinkDialog] = useState({ open: false, user: null, link: '', loading: false });
  const [menuFormLinkDialog, setMenuFormLinkDialog] = useState({ open: false, user: null, link: '', loading: false });
  const [editDialogUser, setEditDialogUser] = useState(null);
  const [editUsername, setEditUsername] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [resetDialogUser, setResetDialogUser] = useState(null);
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetSaving, setResetSaving] = useState(false);
  const [resetEmailSending, setResetEmailSending] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm({ mode: 'onTouched' });

  useEffect(() => { dispatch(getUsers(user?._id)); }, []);

  // 승인 요청 메일 링크 (/user?approve=<id>) → 목록이 오면 그 사람의 승인 창을 바로 연다
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const id = searchParams.get('approve');
    if (!id || !users?.length) return;
    const target = users.find((u) => String(u._id) === id);
    if (target && target.status === 'pending') openApproveDialog(target);
    searchParams.delete('approve');
    setSearchParams(searchParams, { replace: true });
  }, [users, searchParams, setSearchParams]); // eslint-disable-line react-hooks/exhaustive-deps -- openApproveDialog 는 상태 setter 만 호출

  useEffect(() => {
    if (isUserAdded) { toast.success('유저가 추가되었습니다.'); dispatch(userFlagOff()); }
    if (isAlreadyAvailable) { toast.error('이미 등록된 이메일입니다.'); dispatch(userFlagOff()); }
    if (isUserDeleted) { toast.success('유저가 삭제되었습니다.'); dispatch(userFlagOff()); }
  }, [isUserAdded, isAlreadyAvailable, isUserDeleted]);

  const submitUser = (data) => {
    const payload = { email: data.email, username: data.username, role, user: user._id };
    if (usePasswordMode) payload.password = data.password;
    else payload.sendSetupEmail = true;
    dispatch(userRegister(payload));
    setOpenForm(false);
    reset();
  };

  const openApproveDialog = (u) => {
    setApproveDialogUser(u);
    setApproveRole(u.role === 'admin' ? 'store_owner' : (u.role || 'store_owner'));
    // 점주는 기본 '새 점포 온보딩', 매니저는 '기존 매장 연결' (12.01-D011)
    setApproveMode(u.role === 'store_manager' || u.signupStoreType === 'existing' ? 'link' : 'onboard');
    setApproveStoreId('');
    api.get(`/store/getall/${user?._id}`).then(({ data }) => setApproveStores(data.stores || [])).catch(() => setApproveStores([]));
  };
  const handleApproveConfirm = async () => {
    if (!approveDialogUser) return;
    const storeRole = approveRole === 'store_owner' || approveRole === 'store_manager';
    const mode = storeRole ? approveMode : 'none';
    if (mode === 'link' && !approveStoreId) { toast.error('연결할 매장을 고르세요'); return; }
    try {
      await dispatch(approveUser(approveDialogUser._id, approveRole, {
        mode, storeId: mode === 'link' ? approveStoreId : undefined,
        linkAs: approveRole === 'store_manager' ? 'manager' : 'owner',
      }));
    } catch (e) { toast.error(e.response?.data?.message || '승인 실패'); return; }
    await dispatch(getUsers(user?._id));
    if (mode === 'onboard') {
      toast.success('승인 완료 — 새 점포 온보딩을 시작했습니다');
      navigate('/onboarding');
    } else {
      toast.success(mode === 'link' ? '승인 완료 — 매장에 연결했습니다' : '승인 완료 (그룹 지정됨)');
    }
    setApproveDialogUser(null);
  };
  const handleReject = async (id) => {
    await dispatch(rejectUser(id));
    await dispatch(getUsers(user?._id));
    toast.success('거절 완료');
  };
  const openRoleDialog = (u) => { setRoleDialogUser(u); setNewRole(u.role || 'user'); };
  const openFormLinkDialog = async (row) => {
    setFormLinkDialog({ open: true, user: row, link: '', loading: true });
    try {
      const link = await dispatch(createStoreFormLink(row._id));
      setFormLinkDialog(f => ({ ...f, link: link || '', loading: false }));
      if (!link) toast.error('링크 생성에 실패했습니다.');
    } catch (e) {
      setFormLinkDialog(f => ({ ...f, link: '', loading: false }));
      toast.error('링크 생성에 실패했습니다.');
    }
  };
  const copyFormLink = () => {
    if (formLinkDialog.link) {
      navigator.clipboard.writeText(formLinkDialog.link);
      toast.success('링크가 복사되었습니다.');
    }
  };
  const openMenuFormLinkDialog = async (row) => {
    setMenuFormLinkDialog({ open: true, user: row, link: '', loading: true });
    try {
      const link = await dispatch(createMenuFormLink(row._id));
      setMenuFormLinkDialog(f => ({ ...f, link: link || '', loading: false }));
      if (!link) toast.error('링크 생성에 실패했습니다.');
    } catch (e) {
      setMenuFormLinkDialog(f => ({ ...f, link: '', loading: false }));
      toast.error('링크 생성에 실패했습니다.');
    }
  };
  const copyMenuFormLink = () => {
    if (menuFormLinkDialog.link) {
      navigator.clipboard.writeText(menuFormLinkDialog.link);
      toast.success('링크가 복사되었습니다.');
    }
  };
  const handleRoleSave = async () => {
    await dispatch(changeUserRole(roleDialogUser._id, newRole));
    await dispatch(getUsers(user?._id));
    toast.success('권한이 변경되었습니다.');
    setRoleDialogUser(null);
  };

  const openEditDialog = (row) => {
    setEditDialogUser(row);
    setEditUsername(row.username || '');
    setEditEmail(row.email || '');
  };
  const handleEditSave = async () => {
    if (!editDialogUser) return;
    if (!editUsername?.trim() || !editEmail?.trim()) {
      toast.error('이름과 이메일을 입력하세요.');
      return;
    }
    setEditSaving(true);
    try {
      await dispatch(updateUser(editDialogUser._id, { username: editUsername.trim(), email: editEmail.trim() }));
      dispatch(getUsers(user?._id));
      toast.success('수정되었습니다.');
      setEditDialogUser(null);
    } catch (e) {
      toast.error(e?.response?.data?.message || '수정에 실패했습니다.');
    } finally {
      setEditSaving(false);
    }
  };

  const openResetDialog = (row) => {
    setResetDialogUser(row);
    setResetNewPassword('');
  };
  const handleResetSavePassword = async () => {
    if (!resetDialogUser) return;
    if (!resetNewPassword || resetNewPassword.length < 6) {
      toast.error('비밀번호는 6자 이상이어야 합니다.');
      return;
    }
    setResetSaving(true);
    try {
      await dispatch(updateUser(resetDialogUser._id, { password: resetNewPassword }));
      dispatch(getUsers(user?._id));
      toast.success('비밀번호가 변경되었습니다.');
      setResetDialogUser(null);
    } catch (e) {
      toast.error(e?.response?.data?.message || '변경에 실패했습니다.');
    } finally {
      setResetSaving(false);
    }
  };
  const handleResetSendEmail = async () => {
    if (!resetDialogUser) return;
    setResetEmailSending(true);
    try {
      const data = await dispatch(adminSendResetEmail(resetDialogUser._id));
      toast.success(`비밀번호 재설정 이메일을 ${data?.email || resetDialogUser.email}로 발송했습니다.`);
      setResetDialogUser(null);
    } catch (e) {
      toast.error(e?.response?.data?.message || '이메일 발송에 실패했습니다.');
    } finally {
      setResetEmailSending(false);
    }
  };

  const pendingUsers = users.filter(u => u.status === 'pending');

  const filteredUsers = users.filter(u => {
    const matchStatus = statusFilter === 'all' || u.status === statusFilter;
    const q = searchText.toLowerCase();
    const matchSearch = !q || u.username?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  const usersByGroup = GROUP_ORDER.map(groupKey => ({
    key: groupKey,
    name: ROLE_MAP[groupKey] || groupKey,
    users: filteredUsers.filter(u => (u.role || 'user') === groupKey),
  }));

  const statCounts = {
    all: users.length,
    pending: users.filter(u => u.status === 'pending').length,
    approved: users.filter(u => u.status === 'approved').length,
    rejected: users.filter(u => u.status === 'rejected').length,
  };

  return (
    <Layout title="User" user={user}>
      <Box sx={{ p: 2 }}>

        {/* ── Pending approval banner ── */}
        {pendingUsers.length > 0 && (
          <Box sx={{
            background: 'linear-gradient(135deg, #fff8e1 0%, #fffde7 100%)',
            border: '1.5px solid #f9a825',
            borderRadius: 3, p: 2, mb: 2,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <PersonAddIcon sx={{ color: '#f57f17', fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, color: '#f57f17', fontSize: 14 }}>
                승인 대기 중인 계정 {pendingUsers.length}개
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {pendingUsers.map(u => (
                <Box key={u._id} sx={{
                  display: 'flex', alignItems: 'center', gap: 2,
                  background: 'white', borderRadius: 2, p: 1.5,
                  border: '1px solid #ffe082', flexWrap: 'wrap',
                }}>
                  <Box sx={{ flex: 1, minWidth: 120 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#222' }}>{u.username}</Typography>
                    <Typography sx={{ fontSize: 11, color: '#888' }}>{u.email}</Typography>
                    {u.phone && <Typography sx={{ fontSize: 11, color: '#555' }}>Tel: {u.phone}</Typography>}
                    {u.town && <Typography sx={{ fontSize: 11, color: '#555' }}>{u.storeName ? `${u.storeName} — ` : ''}{u.town}, {u.state} {u.zipcode}</Typography>}
                    {u.authProvider === 'google' && <Chip label="Google" size="small" sx={{ height: 18, fontSize: 10, mt: 0.3, background: '#e8f5e9', color: '#2e7d32' }} />}
                  </Box>
                  <Chip
                    label={ROLE_MAP[u.role] || u.role}
                    size="small"
                    sx={{ background: '#f5f3ff', color: '#551FFF', fontWeight: 600 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <button
                      onClick={() => openApproveDialog(u)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '5px 14px', border: 'none', borderRadius: 8,
                        background: '#2e7d32', color: 'white', fontSize: 12,
                        fontWeight: 700, cursor: 'pointer',
                      }}
                    >
                      <CheckCircleIcon sx={{ fontSize: 14 }} /> 승인
                    </button>
                    <button
                      onClick={() => handleReject(u._id)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '5px 14px', border: 'none', borderRadius: 8,
                        background: '#c62828', color: 'white', fontSize: 12,
                        fontWeight: 700, cursor: 'pointer',
                      }}
                    >
                      <CancelIcon sx={{ fontSize: 14 }} /> 거절
                    </button>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* ── Toolbar ── */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
          <Box sx={{
            flex: 1,
            minWidth: { xs: '100%', sm: 200 },
            position: 'relative',
            background: 'white',
            borderRadius: 2,
            border: '1px solid #e0e0e0',
          }}>
            <input
              style={{
                width: '100%',
                boxSizing: 'border-box',
                border: 'none',
                outline: 'none',
                fontSize: 13,
                padding: '10px 42px 10px 12px',
                background: 'transparent',
              }}
              placeholder="이름 또는 이메일 검색"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
            <SearchIcon sx={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#aaa', pointerEvents: 'none', fontSize: 22 }} />
          </Box>

          {/* Status filter tabs */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {[
              { key: 'all', label: `전체 ${statCounts.all}` },
              { key: 'pending',  label: `대기 ${statCounts.pending}` },
              { key: 'approved', label: `승인 ${statCounts.approved}` },
              { key: 'rejected', label: `거절 ${statCounts.rejected}` },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                style={{
                  padding: '7px 14px', border: `1.5px solid ${statusFilter === f.key ? '#551FFF' : '#e0e0e0'}`,
                  borderRadius: 8, background: statusFilter === f.key ? '#551FFF' : 'white',
                  color: statusFilter === f.key ? 'white' : '#666',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                {f.label}
              </button>
            ))}
          </Box>

          <button
            onClick={() => setOpenForm(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '9px 18px', background: '#551FFF', color: 'white',
              border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700,
              cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
            }}
          >
            <AddIcon sx={{ fontSize: 16 }} /> 유저 추가
          </button>
        </Box>

        {/* ── User table (그룹별: Admin, Designer, Store owner, Store manager, TLJ admin, TLJ Manager) ── */}
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflowX: 'hidden', maxWidth: '100%' }}>
          <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow sx={{ background: '#f5f3ff' }}>
                <TableCell sx={{ fontWeight: 700, width: 36, display: { xs: 'none', sm: 'table-cell' } }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700, width: isMobile ? '22%' : '18%' }}>이름</TableCell>
                <TableCell sx={{ fontWeight: 700, width: isMobile ? '30%' : '26%' }}>이메일</TableCell>
                <TableCell sx={{ fontWeight: 700, width: isMobile ? '18%' : '14%', display: { xs: 'none', md: 'table-cell' } }}>역할</TableCell>
                <TableCell sx={{ fontWeight: 700, width: isMobile ? '16%' : '12%' }}>상태</TableCell>
                <TableCell sx={{ fontWeight: 700, display: { xs: 'none', lg: 'table-cell' } }}>가입일</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: isMobile ? '34%' : '15%' }}>관리</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#aaa' }}>
                    {searchText ? '검색 결과가 없습니다.' : '등록된 유저가 없습니다.'}
                  </TableCell>
                </TableRow>
              )}
              {usersByGroup.map(({ key: groupKey, name: groupName, users: groupUsers }) =>
                groupUsers.length > 0 ? (
                  <Fragment key={groupKey}>
                    <TableRow sx={{ background: 'rgba(85,31,255,0.06)' }}>
                      <TableCell colSpan={7} sx={{ fontWeight: 700, fontSize: { xs: 11, sm: 12 }, color: '#551FFF', py: { xs: 0.75, sm: 1 } }}>
                        {groupName}
                      </TableCell>
                    </TableRow>
                    {groupUsers.map((row, idx) => (
                      <TableRow
                        key={row._id}
                        hover
                        sx={{
                          background: row.status === 'pending' ? '#fffde7' : row.status === 'rejected' ? '#fff5f5' : 'white',
                        }}
                      >
                        <TableCell sx={{ color: '#888', fontSize: 11, display: { xs: 'none', sm: 'table-cell' } }}>{idx + 1}</TableCell>
                        <TableCell sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: { xs: 12, sm: 13 }, lineHeight: 1.25, wordBreak: 'break-word' }}>{row.username}</Typography>
                          <Typography sx={{ display: { xs: 'block', md: 'none' }, fontSize: 10, color: '#6b4ef7', fontWeight: 600, mt: 0.3, lineHeight: 1.2 }}>
                            {ROLE_MAP[row.role] || row.role}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: 11, color: '#555', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.email}>{row.email}</TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          <Chip
                            label={ROLE_MAP[row.role] || row.role}
                            size="small"
                            sx={{ background: '#f5f3ff', color: '#551FFF', fontWeight: 600, fontSize: { xs: 10, sm: 11 }, maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', lineHeight: 1.2 } }}
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 0 }}>
                          <StatusChip status={row.status || 'approved'} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 10, color: '#888', display: { xs: 'none', lg: 'table-cell' } }}>
                          {new Date(row.createdAt).toLocaleDateString('ko-KR')}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top', p: { xs: 0.75, sm: 1 } }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'stretch', minWidth: 0 }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                      {row.status === 'pending' && (
                        <>
                          <Tooltip title="승인 (그룹 지정)">
                            <button
                              type="button"
                              onClick={() => openApproveDialog(row)}
                              style={{ background: '#e8f5e9', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: '#2e7d32', fontSize: 10, fontWeight: 700 }}
                            >승인</button>
                          </Tooltip>
                                <Tooltip title="거절">
                                  <button
                                    type="button"
                                    onClick={() => handleReject(row._id)}
                                    style={{ background: '#fce4ec', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: '#c62828', fontSize: 10, fontWeight: 700 }}
                                  >거절</button>
                                </Tooltip>
                              </>
                            )}
                      {row.status === 'rejected' && (
                        <Tooltip title="재승인 (그룹 지정)">
                          <button
                            type="button"
                            onClick={() => openApproveDialog(row)}
                                  style={{ background: '#e8f5e9', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: '#2e7d32', fontSize: 10, fontWeight: 700 }}
                                >재승인</button>
                              </Tooltip>
                            )}
                            {row.role === 'store_owner' && (
                              <>
                                <Tooltip title="스토어 폼 링크 생성">
                                  <button
                                    type="button"
                                    onClick={() => openFormLinkDialog(row)}
                                    style={{ background: '#e3f2fd', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: '#1565c0', fontSize: 10, fontWeight: 700 }}
                                  >폼</button>
                                </Tooltip>
                                <Tooltip title="메뉴보드 폼 링크 생성">
                                  <button
                                    type="button"
                                    onClick={() => openMenuFormLinkDialog(row)}
                                    style={{ background: '#f3e5f5', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: '#7b1fa2', fontSize: 10, fontWeight: 700 }}
                                  >메뉴</button>
                                </Tooltip>
                              </>
                            )}
                            <Tooltip title="편집 (이름·이메일)">
                              <button
                                type="button"
                                onClick={() => openEditDialog(row)}
                                style={{ background: '#e8eaf6', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: '#3949ab', fontSize: 10, fontWeight: 700 }}
                              >편집</button>
                            </Tooltip>
                            <Tooltip title="권한 변경">
                              <button
                                type="button"
                                onClick={() => openRoleDialog(row)}
                                style={{ background: '#f5f3ff', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: '#551FFF', fontSize: 10, fontWeight: 700 }}
                              >권한</button>
                            </Tooltip>
                            <Tooltip title="삭제">
                              <button
                                type="button"
                                onClick={() => { setDeleteId(row._id); setOpenDeleteModel(true); }}
                                style={{ background: '#fce4ec', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: '#c62828', fontSize: 10, fontWeight: 700 }}
                              >삭제</button>
                            </Tooltip>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center', pt: 0.25, borderTop: '1px solid #f0f0f0' }}>
                            <Tooltip title="비밀번호 초기화">
                              <button
                                type="button"
                                onClick={() => openResetDialog(row)}
                                style={{ background: '#fff3e0', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: '#e65100', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}
                              >비밀번호</button>
                            </Tooltip>
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </Fragment>
                ) : null
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* ── Delete confirm ── */}
      <DeleteModel
        open={openDeleteModel}
        onClose={v => setOpenDeleteModel(v)}
        onConfirm={() => { dispatch(deleteUser(deleteId)); setOpenDeleteModel(false); }}
      />

      {/* ── Role change dialog ── */}
      <Dialog open={!!roleDialogUser} onClose={() => setRoleDialogUser(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#551FFF' }}>권한 변경</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: '#666', mb: 2 }}>
            {roleDialogUser?.username} ({roleDialogUser?.email})
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>역할</InputLabel>
            <Select label="역할" value={newRole} onChange={e => setNewRole(e.target.value)}>
              {ROLES.map(r => (
                <MenuItem key={r} value={r}>{ROLE_MAP[r] || r}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRoleDialogUser(null)} sx={{ color: '#888' }}>취소</Button>
          <Button variant="contained" onClick={handleRoleSave} sx={{ background: '#551FFF', borderRadius: 2 }}>저장</Button>
        </DialogActions>
      </Dialog>

      {/* ── 유저 정보 편집 (이름·이메일) ── */}
      <Dialog open={!!editDialogUser} onClose={() => setEditDialogUser(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#551FFF' }}>유저 정보 편집</DialogTitle>
        <DialogContent>
          {editDialogUser && (
            <>
              <TextField fullWidth size="small" label="이름" value={editUsername} onChange={e => setEditUsername(e.target.value)} sx={{ mt: 1, mb: 2 }} />
              <TextField fullWidth size="small" label="이메일" type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialogUser(null)} sx={{ color: '#888' }}>취소</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editSaving} sx={{ background: '#551FFF', borderRadius: 2 }}>{editSaving ? '저장 중...' : '저장'}</Button>
        </DialogActions>
      </Dialog>

      {/* ── 비밀번호 초기화 ── */}
      <Dialog open={!!resetDialogUser} onClose={() => setResetDialogUser(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#e65100' }}>비밀번호 초기화</DialogTitle>
        <DialogContent>
          {resetDialogUser && (
            <Box sx={{ pt: 1 }}>
              <Typography sx={{ fontSize: 13, color: '#666', mb: 2 }}>{resetDialogUser.username} ({resetDialogUser.email})</Typography>
              <TextField fullWidth size="small" label="새 비밀번호 (6자 이상)" type="password" value={resetNewPassword} onChange={e => setResetNewPassword(e.target.value)} sx={{ mb: 2 }} />
              <Button fullWidth variant="contained" onClick={handleResetSavePassword} disabled={resetSaving} sx={{ background: '#e65100', borderRadius: 2, mb: 2 }}>{resetSaving ? '저장 중...' : '비밀번호 저장 (즉시 반영)'}</Button>
              <Typography sx={{ fontSize: 12, color: '#888', mb: 1 }}>또는</Typography>
              <Button fullWidth variant="outlined" onClick={handleResetSendEmail} disabled={resetEmailSending} sx={{ borderRadius: 2 }}>{resetEmailSending ? '발송 중...' : '비밀번호 재설정 이메일 발송'}</Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setResetDialogUser(null)}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* ── 메뉴보드 폼 링크 (Store owner용) ── */}
      <Dialog open={menuFormLinkDialog.open} onClose={() => setMenuFormLinkDialog({ open: false, user: null, link: '', loading: false })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#7b1fa2' }}>메뉴보드 폼 링크</DialogTitle>
        <DialogContent>
          {menuFormLinkDialog.user && (
            <Typography sx={{ fontSize: 13, color: '#666', mb: 2 }}>
              {menuFormLinkDialog.user.username} ({menuFormLinkDialog.user.email}) — 고객에게 전달할 메뉴 가격 입력 링크
            </Typography>
          )}
          {menuFormLinkDialog.loading ? (
            <Typography sx={{ color: '#888' }}>링크 생성 중...</Typography>
          ) : menuFormLinkDialog.link ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField fullWidth size="small" value={menuFormLinkDialog.link} readOnly sx={{ '& .MuiInputBase-input': { fontSize: 12 } }} />
              <Button variant="contained" onClick={copyMenuFormLink} sx={{ background: '#7b1fa2', borderRadius: 2, flexShrink: 0 }}>복사</Button>
            </Box>
          ) : !menuFormLinkDialog.loading && menuFormLinkDialog.open && !menuFormLinkDialog.link ? (
            <Typography sx={{ color: '#c62828', fontSize: 13 }}>링크를 생성할 수 없습니다.</Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMenuFormLinkDialog({ open: false, user: null, link: '', loading: false })}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* ── 스토어 폼 링크 (Store owner용) ── */}
      <Dialog open={formLinkDialog.open} onClose={() => setFormLinkDialog({ open: false, user: null, link: '', loading: false })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#551FFF' }}>스토어 폼 링크</DialogTitle>
        <DialogContent>
          {formLinkDialog.user && (
            <Typography sx={{ fontSize: 13, color: '#666', mb: 2 }}>
              {formLinkDialog.user.username} ({formLinkDialog.user.email}) — 고객에게 전달할 링크
            </Typography>
          )}
          {formLinkDialog.loading ? (
            <Typography sx={{ color: '#888' }}>링크 생성 중...</Typography>
          ) : formLinkDialog.link ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField fullWidth size="small" value={formLinkDialog.link} readOnly sx={{ '& .MuiInputBase-input': { fontSize: 12 } }} />
              <Button variant="contained" onClick={copyFormLink} sx={{ background: '#551FFF', borderRadius: 2, flexShrink: 0 }}>복사</Button>
            </Box>
          ) : !formLinkDialog.loading && formLinkDialog.open && !formLinkDialog.link ? (
            <Typography sx={{ color: '#c62828', fontSize: 13 }}>링크를 생성할 수 없습니다.</Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFormLinkDialog({ open: false, user: null, link: '', loading: false })}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* ── Approve with group (역할) 지정 ── */}
      <Dialog open={!!approveDialogUser} onClose={() => setApproveDialogUser(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#2e7d32' }}>승인 및 그룹 지정</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: '#666', mb: 2 }}>
            {approveDialogUser?.username} ({approveDialogUser?.email}) — 어드민/디자이너가 그룹을 지정해 주세요.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>그룹(역할)</InputLabel>
            <Select label="그룹(역할)" value={approveRole} onChange={e => setApproveRole(e.target.value)}>
              {ROLES.filter(r => r !== 'admin').map(r => (
                <MenuItem key={r} value={r}>{ROLE_MAP[r] || r}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {(approveRole === 'store_owner' || approveRole === 'store_manager') && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>매장</Typography>
              {approveDialogUser?.storeName && (
                <Typography sx={{ fontSize: 12, color: '#666', mb: 0.5 }}>
                  가입 때 적은 매장: {approveDialogUser.storeName}{approveDialogUser.town ? ` · ${approveDialogUser.town}, ${approveDialogUser.state || ''}` : ''}{approveDialogUser.signupScreenCount ? ` · TV ${approveDialogUser.signupScreenCount}대` : ''}
                </Typography>
              )}
              <RadioGroup value={approveMode} onChange={e => setApproveMode(e.target.value)}>
                {approveRole === 'store_owner' && (
                  <FormControlLabel value="onboard" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13 }}>새 점포로 온보딩 시작 (계약서는 온보딩 보드에서 발송)</Typography>} />
                )}
                <FormControlLabel value="link" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13 }}>기존 매장에 연결 ({approveRole === 'store_manager' ? '매니저' : '점주'})</Typography>} />
                <FormControlLabel value="none" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13 }}>매장 없이 승인만</Typography>} />
              </RadioGroup>
              {approveMode === 'link' && (
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <InputLabel>매장 선택</InputLabel>
                  <Select label="매장 선택" value={approveStoreId} onChange={e => setApproveStoreId(e.target.value)}>
                    {approveStores.map(st => (
                      <MenuItem key={st._id} value={st._id}>{st.name}{st.tljNo ? ` (${st.tljNo})` : ''}{st.town ? ` · ${st.town}` : ''}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setApproveDialogUser(null)} sx={{ color: '#888' }}>취소</Button>
          <Button variant="contained" onClick={handleApproveConfirm} sx={{ background: '#2e7d32', borderRadius: 2 }}>승인</Button>
        </DialogActions>
      </Dialog>

      {/* ── Add user dialog ── */}
      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, borderBottom: '2px solid #551FFF', color: '#551FFF' }}>
          유저 추가
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <form id="add-user-form" onSubmit={handleSubmit(submitUser)}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                {...register('username', { required: '이름을 입력하세요.' })}
                label="이름 *" size="small" fullWidth
                error={!!errors.username}
                helperText={errors.username?.message}
              />
              <TextField
                {...register('email', { required: '이메일을 입력하세요.' })}
                label="이메일 *" type="email" size="small" fullWidth
                error={!!errors.email}
                helperText={errors.email?.message}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={usePasswordMode}
                    onChange={(e) => setUsePasswordMode(e.target.checked)}
                    size="small"
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#551FFF' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#551FFF' } }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: 13, color: '#555' }}>
                    {usePasswordMode ? '비밀번호 직접 설정' : '고객에게 초기 비밀번호 설정 이메일 발송'}
                  </Typography>
                }
              />
              {usePasswordMode ? (
                <TextField
                  {...register('password', { required: '비밀번호를 입력하세요.' })}
                  label="비밀번호 *"
                  type={showPassword ? 'text' : 'password'}
                  size="small" fullWidth
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(v => !v)} edge="end">
                          {showPassword ? <VisibilityOff sx={{ color: '#551FFF' }} /> : <Visibility sx={{ color: '#551FFF' }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              ) : (
                <Typography sx={{ fontSize: 12, color: '#f57f17', background: '#fff8e1', borderRadius: 1.5, p: 1.2 }}>
                  고객 이메일로 <strong>초기 비밀번호 설정 링크</strong>가 발송됩니다. (7일 유효)
                </Typography>
              )}
              <FormControl size="small" fullWidth>
                <InputLabel>역할</InputLabel>
                <Select label="역할" value={role} onChange={e => setRole(e.target.value)}>
                  {ROLES.map(r => (
                    <MenuItem key={r} value={r}>{ROLE_MAP[r] || r}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography sx={{ fontSize: 12, color: '#888', background: '#f5f3ff', borderRadius: 1.5, p: 1.2 }}>
                추가된 유저는 <strong>승인 대기</strong> 상태로 생성됩니다. 유저 목록에서 승인 시 그룹을 지정해 주세요.
              </Typography>
            </Box>
          </form>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setOpenForm(false); reset(); }} sx={{ color: '#888' }}>취소</Button>
          <Button form="add-user-form" type="submit" variant="contained" sx={{ background: '#551FFF', borderRadius: 2 }}>
            추가
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

export default User;
